import { useFrame } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  ComponentSurfaceWrappingMode,
  useSurfaceProps,
} from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { ComponentSurfaceMeshData } from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { Color } from 'three'

/**
 * The base layer for asset rendering; the hoverable part of a component which
 * other layers (like heatmaps) are "overlays" on top of.
 * @constructor
 */
export const BaseLayerChunk3D: React.FC = () => {
  //----------------------------------------------------------------------------

  const { assetColorString, clipConfig, assetOpacity, offscreenRender } =
    useAssetVizInternal()
  const { bounds, geometry, wrapMode, componentModel } = useSurfaceProps()

  const [currentOpacity, setCurrentOpacity] = React.useState<number>(1.0)

  const userData = React.useMemo<ComponentSurfaceMeshData>(
    () => ({
      bounds,
      componentModel,
      doWrapX: wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX,
      doWrapY: wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY,
    }),
    [bounds, wrapMode, componentModel]
  )

  const assetColor = React.useMemo(
    () => new Color(assetColorString).convertSRGBToLinear(),
    [assetColorString]
  )
  const getSurfaceOpacity = React.useCallback(
    () =>
      typeof assetOpacity === 'number'
        ? assetOpacity
        : assetOpacity(componentModel),
    [assetOpacity, componentModel]
  )

  useFrame((_, delta) => {
    const targetOpacity = getSurfaceOpacity()
    // Smoothly interpolate to target opacity
    setCurrentOpacity((prevOpacity) =>
      THREE.MathUtils.lerp(
        prevOpacity,
        targetOpacity,
        offscreenRender ? 1 : Math.min(1, delta * 10.0) // if rendering off-screen we want this transition to be instantaneous
      )
    )
  })

  //----------------------------------------------------------------------------

  return (
    <>
      <mesh
        name={`${bounds.componentID}_base_chunk`}
        layers={RenderLayerID.VisibleCollidable}
        geometry={geometry}
        userData={userData}>
        <meshBasicMaterial
          color={assetColor}
          side={THREE.DoubleSide}
          clippingPlanes={clipConfig.planes}
          polygonOffset={true}
          polygonOffsetFactor={10}
          polygonOffsetUnits={10}
          opacity={currentOpacity}
          transparent
        />
      </mesh>
    </>
  )
}
